import { useEffect, useState } from "react";
import { AlertCircle, CheckCircle, Loader2, Receipt, Wallet } from "lucide-react";

import {
  useGetMyFirmsQuery,
  useGetMyFirmQuery,
  usePayMyFirmDuesMutation,
} from "../api/firmApi";
import { submitOnePayForm } from "../../logbook/utils/onepay";
import { formatCurrency, formatDate } from "../../../utils/formatters";

/**
 * What a firm owes, and the way to pay it.
 *
 * The same firm MyFirmPage shows, narrowed to the money — this is the page a
 * firm's own account is sent to when it comes to settle, and the one its menu
 * calls "Invoices & Payment". The particulars, the roster and the registration
 * details live on /my-firm; repeating them here would bury what somebody opened
 * this page to do.
 *
 * The invoices are the FIRM's, never the owner's: a member who is a principal
 * partner sees them here too, and paying one does nothing to their own dues.
 * One payment clears the lot — the server allocates it across the invoices, so
 * there is deliberately no per-invoice button to choose between.
 */
export default function MyFirmInvoicesPage() {
  const { data: firms = [], isLoading, isError, error: listError } = useGetMyFirmsQuery();
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // One firm is the common case — open it without making anyone choose.
  useEffect(() => {
    if (!selectedId && firms.length > 0) setSelectedId(firms[0].id);
  }, [firms, selectedId]);

  const { data: firm, isFetching } = useGetMyFirmQuery(selectedId as number, {
    skip: !selectedId,
  });

  const [pay, { isLoading: paying }] = usePayMyFirmDuesMutation();
  const [error, setError] = useState<string | null>(null);

  /**
   * Hand off to OnePay through the SHARED helper — the gateway expects its
   * fields under their own names, and a form built by hand arrives carrying no
   * merchant id, which the gateway reports as "INVALID Merchant/Caller".
   */
  const payDues = async () => {
    if (!firm) return;
    setError(null);
    try {
      const res: any = await pay({ id: firm.id }).unwrap();

      if (!res?.payment_url) {
        throw new Error(res?.message || "Could not start the payment.");
      }

      submitOnePayForm(res);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || "Could not start the payment. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6 py-24 flex flex-col items-center gap-3 text-gray-400">
        <Loader2 className="w-7 h-7 animate-spin" />
        <p className="text-sm">Loading your invoices…</p>
      </div>
    );
  }

  // A failed request is not "you owe nothing". Saying so would be a claim about
  // the firm's money made on the strength of an answer that never came.
  if (isError) {
    const status = (listError as any)?.status;
    const detail = (listError as any)?.data?.message;

    return (
      <div className="max-w-3xl mx-auto p-4 md:p-6">
        <div className="bg-white border rounded-xl p-8 text-center space-y-3">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto" />
          <h1 className="text-lg font-bold text-gray-800">Could not load your invoices</h1>
          <p className="text-sm text-gray-500">
            {detail ||
              "The register did not answer. This is a problem with the request, not with your account — please try again, and tell the secretariat if it persists."}
          </p>
          {status && (
            <p className="text-[11px] font-mono text-gray-400">
              {String(status)} · GET /firm/my-firms
            </p>
          )}
        </div>
      </div>
    );
  }

  if (firms.length === 0) {
    return (
      <div className="max-w-3xl mx-auto p-4 md:p-6">
        <div className="bg-white border rounded-xl p-8 text-center space-y-3">
          <Receipt className="w-8 h-8 text-gray-300 mx-auto" />
          <h1 className="text-lg font-bold text-gray-800">No firm is registered to you</h1>
          <p className="text-sm text-gray-500">
            This page shows what a practice firm owes. If you believe a firm should be here, ask the
            secretariat to attach your account to it.
          </p>
        </div>
      </div>
    );
  }

  const owed = Number(firm?.outstanding_kobo ?? 0);
  const invoices: any[] = Array.isArray(firm?.invoices) ? firm.invoices : [];

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-5">
      {/* More than one firm is rare; a picker only appears when it is real. */}
      {firms.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {firms.map((f: any) => (
            <button
              key={f.id}
              onClick={() => setSelectedId(f.id)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${
                selectedId === f.id
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              {f.firm_no || f.name}
            </button>
          ))}
        </div>
      )}

      <div>
        <h1 className="text-xl font-bold text-gray-900">Invoices & Payment</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {firm?.name ?? "—"}
          {firm?.firm_no ? ` · ${firm.firm_no}` : ""}
        </p>
      </div>

      {/* The balance, and the one button that clears it. */}
      <div className="bg-white border rounded-xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
              Total outstanding
            </p>
            <p className={`text-3xl font-bold mt-1 ${owed > 0 ? "text-red-600" : "text-green-600"}`}>
              {formatCurrency(owed)}
            </p>
            <p className="text-[11px] text-gray-400 mt-1">
              Billed to the firm. One payment settles every invoice below.
            </p>
            <p className="text-[11px] text-gray-400">
              Paying at the bank instead? Take the teller slip to the secretariat and they will
              record it against the firm.
            </p>
          </div>

          {owed > 0 ? (
            <button
              onClick={payDues}
              disabled={paying || isFetching}
              className="px-6 py-3 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-40 transition-colors inline-flex items-center gap-2"
            >
              <Wallet className="w-4 h-4" />
              {paying ? "Starting payment…" : `Pay ${formatCurrency(owed)}`}
            </button>
          ) : (
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-green-700">
              <CheckCircle className="w-4 h-4" /> Nothing outstanding
            </span>
          )}
        </div>

        {error && (
          <div className="mt-4 flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {error}
          </div>
        )}
      </div>

      {/* Every invoice behind that figure, down to the item — a firm paying a
          bill is owed the sight of what it is paying for. */}
      {invoices.length === 0 ? (
        <div className="bg-white border rounded-xl p-8 text-center">
          <CheckCircle className="w-7 h-7 text-green-500 mx-auto mb-2" />
          <p className="text-sm font-semibold text-gray-800">No invoice is outstanding</p>
          <p className="text-xs text-gray-500 mt-1">
            Nothing is owed on this firm at present. New dues appear here when the secretariat
            raises them.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {invoices.map((inv: any) => (
            <div key={inv.id} className="bg-white border rounded-xl overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b bg-gray-50">
                <div>
                  <div className="font-bold text-gray-800 text-sm">{inv.invoice_number}</div>
                  <div className="text-[11px] text-gray-400 mt-0.5">
                    {String(inv.source ?? "").replace(/_/g, " ") || "dues"}
                    {inv.due_date ? ` · due ${formatDate(inv.due_date)}` : ""}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Balance
                  </div>
                  <div className="text-lg font-bold text-gray-900">
                    {formatCurrency(inv.balance)}
                  </div>
                  {Number(inv.amount_paid) > 0 && (
                    <div className="text-[11px] text-gray-400">
                      {formatCurrency(inv.amount_paid)} of {formatCurrency(inv.total_amount)} paid
                    </div>
                  )}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="px-5 py-2 font-medium">Charge</th>
                      <th className="px-5 py-2 font-medium">Year</th>
                      <th className="px-5 py-2 font-medium text-right">Amount</th>
                      <th className="px-5 py-2 font-medium text-right">Outstanding</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(inv.items ?? []).map((item: any) => (
                      <tr key={item.id}>
                        <td className="px-5 py-3 text-gray-800">{item.description}</td>
                        <td className="px-5 py-3 text-gray-500">{item.year || "—"}</td>
                        <td className="px-5 py-3 text-right text-gray-600">
                          {formatCurrency(item.amount)}
                        </td>
                        <td className="px-5 py-3 text-right font-semibold text-gray-800">
                          {formatCurrency(item.balance_amount)}
                        </td>
                      </tr>
                    ))}
                    {(inv.items ?? []).length === 0 && (
                      <tr>
                        <td className="px-5 py-3 text-gray-400" colSpan={4}>
                          This invoice carries no itemised breakdown.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
